<template>
  <Popup
    v-model:show="isVisible"
    transition="zoom"
    :click-outside-close="true"
    @update:show="closePopup"
  >
    <!-- Main Canvas -->
    <main class="verification-popup-container mobile-device">
      <!-- Background Decoration -->
      <div class="bg-decoration">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
      </div>

      <!-- Verification Modal Container -->
      <div class="modal-card">
        <!-- Close button standard -->
        <span @click="closePopup" class="icon-wrapper close-btn-wrapper">
          <CloseIcon class="verification-popup__close-icon" />
        </span>

        <!-- Stepper -->
        <div class="stepper">
          <div
            class="step-item"
            :class="{ active: step === 1, completed: step > 1 }"
          >
            <div class="step-badge">
              <span v-if="step > 1" class="check-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
              <span v-else>1</span>
            </div>
            <span class="step-label">Enter Email</span>
          </div>
          <div class="step-line" :class="{ active: step > 1 }"></div>
          <div class="step-item" :class="{ active: step === 2 }">
            <div class="step-badge">2</div>
            <span class="step-label">Verify OTP</span>
          </div>
        </div>

        <!-- State 1: Account Verification -->
        <div v-if="step === 1" class="state-container">
          <div class="title-group">
            <h1>Account Verification</h1>
            <p>
              Please enter your email to use the image upload feature.
            </p>
          </div>
          <div class="form-group">
            <label>Email Address</label>
            <div class="input-wrapper">
              <input
                v-model="email"
                type="email"
                placeholder="user@example.com"
                @keyup.enter="submitEmail"
              />
              <span class="icon-wrapper input-icon">
                <EmailIcon />
              </span>
            </div>
            <button
              class="sib-button sib-button--primary sib-button--md sib-button--width-full sib-button--round-md"
              @click="submitEmail"
              :disabled="loading"
              :class="{ 'sib-button--loading': loading }"
            >
              <span v-if="loading" class="sib-button__loading lds-ring">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </span>
              <span v-else class="sib-button__content">
                Send Code
                <span class="icon-wrapper">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </span>
              </span>
            </button>
            <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>
          </div>
        </div>

        <!-- State 2: OTP Input -->
        <div v-if="step === 2" class="state-container">
          <div class="title-group">
            <!-- <div class="back-btn" @click="step = 1">
              <ArrowLeftIcon class="back-icon" />
              <span>Go Back</span>
            </div> -->
            <h1>Enter Verification Code</h1>
            <p>
              We've sent an OTP code to your email: <span>{{ email }}</span>
            </p>
            <p class="instruction-note">
              (Code expires in 24 hours).
            </p>
          </div>
          <div class="form-group">
            <div class="otp-grid">
              <input
                v-for="(digit, index) in 6"
                :key="index"
                ref="otpInputs"
                v-model="otp[index]"
                @input="handleOtpInput($event, index)"
                @keydown.delete="handleOtpDelete($event, index)"
                @paste="handleOtpPaste($event, index)"
                maxlength="1"
                type="text"
                class="otp-input"
                :disabled="loading"
              />
            </div>
            <div class="otp-status-message">
              <div v-if="loading" class="verification-loading">
                <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
                <span>Verifying...</span>
              </div>
              <p v-else-if="errorMsg" class="error-msg">{{ errorMsg }}</p>
            </div>
            <div class="action-links">
              <button
                class="resend-btn"
                @click="resendOtp"
                :disabled="resendCountdown > 0 || loading"
              >
                <span class="icon-wrapper">
                  <TimerIcon />
                </span>
                {{ resendCountdown > 0 ? 'Resend code (in ' : 'Resend code' }}
                <span v-if="resendCountdown > 0" class="countdown-num"
                  >{{ resendCountdown }}s</span
                >
                {{ resendCountdown > 0 ? ')' : '' }}
              </button>
              <div class="divider"></div>
              <button :disabled="loading" class="change-email-btn" @click="step = 1; errorMsg = ''">
                Change Email
                <!-- <span class="icon-wrapper">
                  <EditIcon />
                </span> -->
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </Popup>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import Http from '@/services/http'
import Popup from '@/ui-kits/Popup.vue'
import CloseIcon from '@/assets/images/icons/icon-close.svg'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import EmailIcon from '@/assets/images/icons/icon-sent.svg'
import TimerIcon from '@/assets/images/icons/icon-message.svg'
import LockIcon from '@/assets/images/icons/verified-badge-icon.svg'
import EditIcon from '@/assets/images/icons/icon-user.svg'
import ArrowRightIcon from '@/assets/images/icons/arrow-right.svg'
import {
  RESPONSE_API_ERROR_MESSAGE,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_POPUP_SHOWN,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_SENT,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_SUCCESS,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_FAILED,
  getShopCode,
} from '@/utils'
import Analytics from '@/services/analytics'

const shopCode = getShopCode()

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  numberUploadAiImg: {
    type: Number,
    default: 0,
  },
  productId: {
    type: [String, Number],
    default: '',
  },
})

const emit = defineEmits(['update:show', 'success'])

const isVisible = ref(props.show)
watch(
  () => props.show,
  (val) => {
    isVisible.value = val
    if (val) {
      step.value = 1
      errorMsg.value = ''
      trackVerificationEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_POPUP_SHOWN)
    }
  }
)

const closePopup = () => {
  isVisible.value = false
  emit('update:show', false)
  errorMsg.value = ''
}

const step = ref(1)
const email = ref('')
const otp = ref(['', '', '', '', '', ''])
const loading = ref(false)
const errorMsg = ref('')
const resendCountdown = ref(0)
const otpInputs = ref<HTMLInputElement[]>([])

let timer: any = null

const trackVerificationEvent = (
  eventName: string,
  options: {
    step?: number | null
    errorMsg?: string | null
  } = {}
) => {
  Analytics.track(eventName, {
    email: email.value || null,
    number_upload_ai: props.numberUploadAiImg,
    product_id: props.productId || null,
    step: options.step || null,
    error_message: options.errorMsg || null,
  } as Record<string, any>)
}

onMounted(() => {
  const anonymousId = Analytics?.getAnonymousId()
  if (anonymousId) {
    Analytics.identify(anonymousId, {
      verification_popup_opened: true,
      last_upload_count: props.numberUploadAiImg,
    })
  }
})

const startTimer = () => {
  resendCountdown.value = 60
  if (timer) clearInterval(timer)
  timer = setInterval(() => {
    if (resendCountdown.value > 0) {
      resendCountdown.value--
    } else {
      clearInterval(timer)
    }
  }, 1000)
}

const backendUrl = import.meta.env.VITE_BACKEND_SERVICE_API_URL || ''

const submitEmail = async () => {
  if (!email.value || !email.value.includes('@')) {
    errorMsg.value = 'Please enter a valid email address'
    return
  }

  errorMsg.value = ''
  loading.value = true
  try {
    const response = await Http.post(
      `${backendUrl}/api/email-otp/send`,
      JSON.stringify({
        email: email.value,
        purpose: 'email_verification',
        shop: shopCode,
      }),
      {
        'Content-Type': 'application/json',
      }
    )
    if (!response?.success) {
      throw new Error(response?.message || RESPONSE_API_ERROR_MESSAGE)
    }
    step.value = 2
    otp.value = ['', '', '', '', '', '']
    errorMsg.value = ''
    trackVerificationEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_SENT, { step: 1 })
    startTimer()
    nextTick(() => {
      if (otpInputs.value[0]) {
        otpInputs.value[0].focus()
      }
    })
  } catch (err: any) {
    errorMsg.value = err.message || RESPONSE_API_ERROR_MESSAGE
    trackVerificationEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_FAILED, {
      step: 1,
      errorMsg: errorMsg.value,
    })
  } finally {
    loading.value = false
  }
}

const resendOtp = () => {
  if (resendCountdown.value > 0) return
  submitEmail()
}

const handleOtpInput = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  if (!/^\d$/.test(value)) {
    otp.value[index] = ''
    return
  }

  if (index < 5 && value) {
    otpInputs.value[index + 1]?.focus()
  }

  if (otp.value.every((d) => d !== '')) {
    confirmOtp()
  }
}

const handleOtpDelete = (event: KeyboardEvent, index: number) => {
  if (!otp.value[index] && index > 0) {
    otpInputs.value[index - 1]?.focus()
  }
}

const handleOtpPaste = (event: ClipboardEvent, startIndex: number) => {
  event.preventDefault()
  const pasteData = event.clipboardData?.getData('text') || ''
  const digits = pasteData.replace(/\D/g, '').split('').slice(0, 6 - startIndex)

  if (digits.length > 0) {
    digits.forEach((digit, i) => {
      const targetIndex = startIndex + i
      if (targetIndex < 6) {
        otp.value[targetIndex] = digit
      }
    })

    const nextIndex = Math.min(startIndex + digits.length, 5)
    otpInputs.value[nextIndex]?.focus()

    if (otp.value.every((d) => d !== '')) {
      confirmOtp()
    }
  }
}

const confirmOtp = async () => {
  const code = otp.value.join('')
  if (code.length < 6) return

  errorMsg.value = ''
  loading.value = true
  try {
    const response = await Http.post(
      `${backendUrl}/api/email-otp/verify`,
      JSON.stringify({
        email: email.value,
        code: code,
        purpose: 'email_verification',
      }),
      {
        'Content-Type': 'application/json',
      }
    )
    if (response?.success) {
      errorMsg.value = ''
      emit('success')
      closePopup()
      trackVerificationEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_SUCCESS, { step: 2 })
    } else {
      throw new Error(response?.message || RESPONSE_API_ERROR_MESSAGE)
    }
  } catch (err: any) {
    errorMsg.value = err.message || RESPONSE_API_ERROR_MESSAGE
    trackVerificationEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_FAILED, {
      step: 2,
      errorMsg: errorMsg.value,
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
.verification-popup-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  .blob {
    position: absolute;
    width: 40%;
    height: 40%;
    border-radius: 50%;
    opacity: 0.2;
    filter: blur(120px);
  }
  .blob-1 {
    top: -10%;
    left: -10%;
    background-color: var(--color-primary);
  }
  .blob-2 {
    bottom: -10%;
    right: -10%;
    background-color: var(--text-color-tertiary);
  }
}

.modal-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 440px;
  background-color: #ffffff;
  border-radius: var(--border-radius-xlarge);
  padding: 40px;
  box-shadow: 0 12px 40px rgba(26, 27, 34, 0.08);
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.close-btn-wrapper {
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 2;

  .verification-popup__close-icon {
    width: 1.25rem;
    height: 1.25rem;
    color: var(--text-color-tertiary);
    transition: color 0.2s;

    &:hover {
      color: var(--text-color-primary);
    }
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .icon-wrapper-brand {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background-color: var(--background-color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);

    .icon-verified {
      font-size: 32px;
    }
  }

  .header-text {
    text-align: right;
    span {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.2em;
      color: var(--text-color-tertiary);
      text-transform: uppercase;
    }
  }
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 0 10px;

  .step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex: 1;
    z-index: 1;

    .step-badge {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      background-color: var(--background-color-primary);
      color: var(--text-color-tertiary);
      transition: all 0.3s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .step-label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-color-tertiary);
      transition: all 0.3s ease;
    }

    &.active {
      .step-badge {
        background-color: var(--color-primary);
        color: #fff;
        box-shadow: 0 4px 10px rgba(56, 186, 114, 0.2);
      }
      .step-label {
        color: var(--color-primary);
      }
    }

    &.completed {
      .step-badge {
        background-color: var(--color-primary);
        color: #fff;
        opacity: 0.8;
      }
      .step-label {
        color: var(--color-primary);
        opacity: 0.6;
      }
    }

    .check-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  .step-line {
    height: 2px;
    flex: 1;
    background-color: var(--border-color);
    margin-top: -24px;
    margin-left: -15px;
    margin-right: -15px;
    transition: all 0.4s ease;
    z-index: 0;

    &.active {
      background-color: var(--color-primary);
      opacity: 0.4;
    }
  }
}

.state-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.title-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  h1 {
    color: var(--color-primary);
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.025em;
  }
  p {
    color: var(--text-color-secondary);
    font-size: 14px;
    line-height: 1.6;
    margin: 0;

    span {
      font-weight: 600;
      color: var(--text-color-primary);
    }
  }

  .instruction-note {
    font-size: 13px;
    color: var(--text-color-tertiary);
    line-height: 1.5;
    margin-top: -4px;
    font-weight: 500;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-primary);
    cursor: pointer;
    margin-bottom: 8px;

    .back-icon {
      width: 14px;
      height: 14px;
      fill: currentColor;
    }

    span:nth-child(2) {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 16px;

  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-secondary);
  }

  .input-wrapper {
    position: relative;

    input {
      width: 100%;
      height: 48px;
      padding: 0 16px;
      padding-right: 48px;
      border-radius: var(--border-radius-large);
      background-color: var(--background-color);
      border: 1px solid transparent;
      color: var(--text-color-primary);
      transition: all 0.2s;
      outline: none;

      &::placeholder {
        color: var(--text-color-tertiary);
      }
      &:focus {
        border-color: var(--color-primary);
        background-color: #fff;
        box-shadow: 0 0 0 2px rgba(56, 186, 114, 0.1);
      }
    }

    .input-icon {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-color-tertiary);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: default;

      svg {
        fill: currentColor;
      }
    }
  }

  .sib-button {
    max-width: none;

    &__content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      white-space: nowrap;
    }
  }
}

.otp-grid {
  display: flex;
  justify-content: space-between;
  gap: 8px;

  .otp-input {
    width: 48px;
    height: 56px;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    color: var(--text-color-primary);
    background-color: var(--background-color);
    border-radius: var(--border-radius-large);
    border: 1px solid transparent;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: var(--color-primary);
      background-color: #fff;
      box-shadow: 0 0 0 2px rgba(56, 186, 114, 0.1);
    }

    &:disabled {
      cursor: default;
      opacity: 0.7;
    }
  }
}

.action-links {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  align-items: center;

  button {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: color 0.2s;
  }

  .resend-btn {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-color-secondary);

    &:hover:not(:disabled) {
      color: var(--color-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    span {
      display: flex;
      align-items: center;
      font-size: 18px;
    }

    .countdown-num {
      font-variant-numeric: tabular-nums;
      font-family: monospace;
      font-size: 15px;
    }
  }

  .divider {
    width: 48px;
    height: 1px;
    background-color: var(--border-color);
  }

  .change-email-btn {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-tertiary);

    &:hover {
      color: var(--text-color-primary);
    }

    span {
      display: flex;
      align-items: center;
      font-size: 14px;
    }

    &:disabled {
      cursor: default;
      color: var(--text-color-tertiary);
    }
  }
}


.modal-footer {
  padding-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0.5;
  color: var(--text-color-primary);

  .icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;

    svg {
      width: 1.25em;
      height: 1.25em;
      fill: currentColor;
    }
  }

  span:nth-child(2) {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
}

.otp-status-message {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 0 -4px;
}

.verification-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--color-primary);
  font-weight: 500;
  font-size: 12px;
  line-height: 1;

  .lds-ring {
    --loading-size: 14px;
    --loading-line-width: 1.5px;
    width: 14px;
    height: 14px;
    color: var(--color-primary);
    
    div {
      width: 14px;
      height: 14px;
      border-width: 1.5px;
    }
  }
}

.error-msg {
  color: var(--color-secondary);
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
  text-align: center;
}
</style>
